import { PreviewLayout } from './types';
import axios from '../utils/axios';

export const DEFAULT_PREVIEW_LAYOUT_ID = 'letter-8up';

export type PreviewLayoutsApi = {
  getPreviewLayouts: (page?: number, pageSize?: number) => Promise<unknown>;
  addPreviewLayout: (payload: PreviewLayout) => Promise<unknown>;
  editPreviewLayout: (payload: PreviewLayout & { previewLayoutId: number }) => Promise<unknown>;
  removePreviewLayout: (previewLayoutId: number) => Promise<unknown>;
  getPreviewLayoutUsage: (previewLayoutId: number) => Promise<unknown>;
};

export type PreviewLayoutUsageEntry = {
  organizationId: number;
  organizationName?: string;
  type: 'Template' | 'Print Run';
  templateId?: number;
  templateName?: string;
  printRunId?: number;
  packageLabel?: string;
  printedDate?: string;
};

type LoadPreviewLayoutsOptions = {
  api?: {
    getPreviewLayouts?: (page?: number, pageSize?: number) => Promise<unknown>;
  };
};

export const isPreviewLayoutApiError = (payload: unknown): payload is { error: true; respBody?: unknown; status?: unknown } => (
  !!payload && typeof payload === 'object' && (payload as { error?: unknown }).error === true
);

export const getPreviewLayoutApiErrorMessage = (payload: { respBody?: unknown; status?: unknown }) => {
  if (typeof payload.respBody === 'string' && payload.respBody.trim()) return payload.respBody;
  if (payload.respBody && typeof payload.respBody === 'object') {
    const message = (payload.respBody as { message?: unknown }).message;
    if (typeof message === 'string' && message.trim()) return message;
  }

  const status = typeof payload.status === 'number' || typeof payload.status === 'string'
    ? String(payload.status).trim()
    : '';
  return `Preview layouts could not be loaded${status ? ` (HTTP ${status})` : ''}.`;
};

const toPositiveId = (value: unknown): number | undefined => {
  if (value == null || (typeof value === 'string' && !value.trim())) return undefined;
  const parsed = Number(value);
  return Number.isSafeInteger(parsed) && parsed > 0 ? parsed : undefined;
};

export const normalizePreviewLayoutUsage = (payload: unknown): PreviewLayoutUsageEntry[] => {
  const list = Array.isArray(payload)
    ? payload
    : (payload as any)?.result ?? (payload as any)?.data ?? payload;
  if (!Array.isArray(list)) return [];

  return list.reduce<PreviewLayoutUsageEntry[]>((acc, raw) => {
    if (!raw || typeof raw !== 'object') return acc;
    const candidate = raw as Record<string, unknown>;
    const type = candidate.type === 'Template' || candidate.type === 'Print Run'
      ? candidate.type
      : undefined;
    const organizationId = toPositiveId(candidate.organizationId);
    if (!type || organizationId == null) return acc;

    const templateId = toPositiveId(candidate.templateId);
    const printRunId = toPositiveId(candidate.printRunId);
    if ((type === 'Template' && templateId == null) || (type === 'Print Run' && printRunId == null)) {
      return acc;
    }

    acc.push({
      organizationId,
      organizationName: typeof candidate.organizationName === 'string'
        ? candidate.organizationName
        : undefined,
      type,
      templateId,
      templateName: typeof candidate.templateName === 'string'
        ? candidate.templateName
        : undefined,
      printRunId,
      packageLabel: typeof candidate.packageLabel === 'string'
        ? candidate.packageLabel
        : undefined,
      printedDate: typeof candidate.printedDate === 'string'
        ? candidate.printedDate
        : undefined,
    });
    return acc;
  }, []);
};

const toNumber = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return Number.NaN;
};

const cleanLabel = (id: string, printer?: string, label?: string) => {
  if (label?.trim()) return label.trim();
  if (printer?.trim()) return `${id} (${printer.trim()})`;
  return id;
};

const mapShape = (shape?: string): PreviewLayout['shape'] => {
  if (!shape) return undefined;
  const s = shape.toLowerCase();
  if (s.includes('circle')) return 'circle';
  if (s.includes('round')) return 'round-rectangle';
  return 'rectangle';
};

export const defaultPreviewLayouts: PreviewLayout[] = [
  {
    id: DEFAULT_PREVIEW_LAYOUT_ID,
    label: 'Letter 8.5" x 11" (8-up)',
    unit: 'in',
    pageWidth: 8.5,
    pageHeight: 11,
    labelWidth: 4.25,
    labelHeight: 2.75,
    marginTop: 0,
    marginRight: 0,
    marginBottom: 0,
    marginLeft: 0,
    spacingX: 0,
    spacingY: 0,
    columns: 2,
    rows: 4,
    format: 'sheets',
    printer: 'generic',
    shape: 'rectangle',
  },
  {
    id: 'letter-single',
    label: 'Letter 8.5" x 11" (1-up)',
    unit: 'in',
    pageWidth: 8.5,
    pageHeight: 11,
    labelWidth: 8.5,
    labelHeight: 11,
    marginTop: 0,
    marginRight: 0,
    marginBottom: 0,
    marginLeft: 0,
    spacingX: 0,
    spacingY: 0,
    columns: 1,
    rows: 1,
    format: 'sheets',
    printer: 'generic',
    shape: 'rectangle',
  },
  {
    id: 'a4-single',
    label: 'A4 210mm x 297mm (1-up)',
    unit: 'cm',
    pageWidth: 21,
    pageHeight: 29.7,
    labelWidth: 21,
    labelHeight: 29.7,
    marginTop: 0,
    marginRight: 0,
    marginBottom: 0,
    marginLeft: 0,
    spacingX: 0,
    spacingY: 0,
    columns: 1,
    rows: 1,
    format: 'sheets',
    printer: 'generic',
    shape: 'rectangle',
  },
  {
    id: 'label4x6-roll',
    label: 'Label 4" x 6" (roll)',
    unit: 'in',
    pageWidth: 4,
    pageHeight: 6,
    labelWidth: 4,
    labelHeight: 6,
    marginTop: 0,
    marginRight: 0,
    marginBottom: 0,
    marginLeft: 0,
    spacingX: 0,
    spacingY: 0,
    columns: 1,
    rows: 1,
    format: 'roll',
    printer: 'generic',
    shape: 'rectangle',
  },
  {
    id: 'label2x3-roll',
    label: 'Label 2" x 3" (roll)',
    unit: 'in',
    pageWidth: 3,
    pageHeight: 2,
    labelWidth: 3,
    labelHeight: 2,
    marginTop: 0,
    marginRight: 0,
    marginBottom: 0,
    marginLeft: 0,
    spacingX: 0,
    spacingY: 0,
    columns: 1,
    rows: 1,
    format: 'roll',
    printer: 'generic',
    shape: 'rectangle',
  },
];

export const normalizePreviewLayouts = (payload: unknown): PreviewLayout[] => {
  if (!Array.isArray(payload)) return [];
  const seen = new Set<string>();

  return payload.reduce<PreviewLayout[]>((acc, raw) => {
    if (!raw || typeof raw !== 'object') return acc;
    const candidate = raw as Record<string, unknown>;
    const id = typeof candidate.id === 'string' ? candidate.id.trim() : '';
    const previewLayoutId = toNumber(candidate.previewLayoutId);
    if (!id || id === 'custom' || seen.has(id)) return acc;

    const unit = candidate.unit === 'cm' || candidate.unit === 'in' || candidate.uom === 'cm' || candidate.uom === 'inches'
      ? (candidate.unit === 'cm' || candidate.uom === 'cm' ? 'cm' : 'in')
      : 'in';

    const pageWidth = toNumber(candidate.pageWidth ?? candidate.sheetLength ?? candidate.width ?? candidate.sheetWidth);
    const pageHeight = toNumber(candidate.pageHeight ?? candidate.sheetHeight ?? candidate.height);
    const labelWidth = toNumber(candidate.labelWidth ?? candidate.labelLength ?? candidate.labelLong);
    const labelHeight = toNumber(candidate.labelHeight ?? candidate.labelShort ?? candidate.labelWidth); // some data repeats width/height
    const marginTop = toNumber(candidate.marginTop ?? candidate.sheetMarginTop ?? candidate.topMargin ?? 0) || 0;
    const marginRight = toNumber(candidate.marginRight ?? candidate.sheetMarginRight ?? candidate.rightMargin ?? 0) || 0;
    const marginBottom = toNumber(candidate.marginBottom ?? candidate.sheetMarginBottom ?? candidate.bottomMargin ?? 0) || 0;
    const marginLeft = toNumber(candidate.marginLeft ?? candidate.sheetMarginLeft ?? candidate.leftMargin ?? 0) || 0;
    // Pitch values from legacy data (authoritative stride between label positions)
    const horizontalPitch = toNumber(candidate.horizontalPitch);
    const verticalPitch = toNumber(candidate.verticalPitch);

    // Prefer explicit spacing fields; derive from pitch as fallback
    const rawSpacingX = toNumber(candidate.spacingX ?? candidate.horizontalSpacing);
    const rawSpacingY = toNumber(candidate.spacingY ?? candidate.verticalSpacing);
    const spacingX = Number.isFinite(rawSpacingX) ? rawSpacingX
      : Number.isFinite(horizontalPitch) ? Math.max(0, horizontalPitch - labelWidth)
      : 0;
    const spacingY = Number.isFinite(rawSpacingY) ? rawSpacingY
      : Number.isFinite(verticalPitch) ? Math.max(0, verticalPitch - labelHeight)
      : 0;

    const columns = toNumber(candidate.columns ?? candidate.horizontalLabelCount);
    const rows = toNumber(candidate.rows ?? candidate.verticalLabelCount);

    if (!Number.isFinite(pageWidth) || !Number.isFinite(pageHeight) || !Number.isFinite(labelWidth) || !Number.isFinite(labelHeight)) return acc;
    if (pageWidth <= 0 || pageHeight <= 0 || labelWidth <= 0 || labelHeight <= 0) return acc;

    const printer = typeof candidate.printer === 'string' ? candidate.printer.trim() : undefined;
    const format = typeof candidate.format === 'string' ? candidate.format.trim() : undefined;
    const shape = mapShape(typeof candidate.shape === 'string' ? candidate.shape : undefined);
    const cornerRadius = toNumber(candidate.cornerRadius);
    const labelRotation = toNumber(candidate.labelRotation);

    const label = cleanLabel(id, printer, typeof candidate.label === 'string' ? candidate.label : undefined);

    seen.add(id);
    acc.push({
      previewLayoutId: Number.isFinite(previewLayoutId) && previewLayoutId > 0 ? previewLayoutId : undefined,
      id,
      label,
      unit,
      pageWidth,
      pageHeight,
      labelWidth,
      labelHeight,
      marginTop,
      marginRight,
      marginBottom,
      marginLeft,
      spacingX,
      spacingY,
      horizontalPitch: Number.isFinite(horizontalPitch) && horizontalPitch > 0 ? horizontalPitch : undefined,
      verticalPitch: Number.isFinite(verticalPitch) && verticalPitch > 0 ? verticalPitch : undefined,
      columns: Number.isFinite(columns) && columns > 0 ? columns : undefined,
      rows: Number.isFinite(rows) && rows > 0 ? rows : undefined,
      printer,
      format,
      shape,
      cornerRadius: Number.isFinite(cornerRadius) && cornerRadius > 0 ? cornerRadius : undefined,
      labelRotation: labelRotation === 90 || labelRotation === 180 || labelRotation === 270 ? labelRotation : undefined,
    });
    return acc;
  }, []);
};

const loadPreviewLayoutsFromEndpoint = async (
  endpoint?: string,
  fallback: PreviewLayout[] = defaultPreviewLayouts,
): Promise<PreviewLayout[]> => {
  if (!endpoint) return fallback;
  const res = await fetch(endpoint);
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  const payload = await res.json();
  const normalized = normalizePreviewLayouts(payload);
  return normalized.length ? normalized : fallback;
};

const loadPreviewLayoutsFromApi = async (
  api: NonNullable<LoadPreviewLayoutsOptions['api']>,
): Promise<PreviewLayout[] | null> => {
  if (!api.getPreviewLayouts) return null;
  const payload = await api.getPreviewLayouts(0, 500);
  if (isPreviewLayoutApiError(payload)) {
    throw new Error(getPreviewLayoutApiErrorMessage(payload));
  }
  const normalized = normalizePreviewLayouts(
    Array.isArray(payload) ? payload : (payload as any)?.result ?? (payload as any)?.data ?? payload,
  );
  return normalized.length ? normalized : null;
};

export const fetchBundledPreviewLayouts = async (
  endpoint?: string,
  fallback: PreviewLayout[] = defaultPreviewLayouts,
): Promise<PreviewLayout[]> => loadPreviewLayoutsFromEndpoint(endpoint, fallback);

export const persistPreviewLayouts = async (
  api: PreviewLayoutsApi,
  layouts: PreviewLayout[],
) => {
  return Promise.all(layouts.map((layout) => api.addPreviewLayout(layout)));
};

export async function getPreviewLayouts(page = 0, pageSize = 500): Promise<unknown> {
  const response = await axios.get('/preview-layout', {
    params: { page, pageSize },
  });
  return response.data;
}

export async function addPreviewLayout(payload: PreviewLayout): Promise<unknown> {
  const response = await axios.post('/preview-layout/add', payload);
  return response.data;
}

export async function editPreviewLayout(payload: PreviewLayout & { previewLayoutId: number }): Promise<unknown> {
  const response = await axios.post('/preview-layout/edit', payload);
  return response.data;
}

export async function removePreviewLayout(previewLayoutId: number): Promise<unknown> {
  const response = await axios.delete('/preview-layout/remove', {
    params: { previewLayoutId },
  });
  return response.data;
}

export async function getPreviewLayoutUsage(previewLayoutId: number): Promise<unknown> {
  const response = await axios.get('/preview-layout/usage', {
    params: { previewLayoutId },
  });
  return response.data;
}

export const loadPreviewLayouts = async (
  endpoint?: string,
  fallback: PreviewLayout[] = defaultPreviewLayouts,
  options: LoadPreviewLayoutsOptions = {},
): Promise<PreviewLayout[]> => {
  const { api } = options;

  try {
    if (api) {
      try {
        const apiLayouts = await loadPreviewLayoutsFromApi(api);
        if (apiLayouts?.length) return apiLayouts;
      } catch (err) {
        console.warn('Preview layout API fetch failed, falling back to bundled layouts', err);
      }
    }

    return await loadPreviewLayoutsFromEndpoint(endpoint, fallback);
  } catch (err) {
    console.warn('Preview layout fetch failed', err);
    return fallback;
  }
};
